'use client';

import React, { useState } from 'react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';

import { useTimePilotT } from '@/i18n/useTimePilotT';
import { cn } from '@/lib/utils';
import { FULLSCREEN_ZOOM_PRESETS } from '@/lib/fullscreen-zoom';

export type MediaDisplayZoomControlsProps = {
  percent: number;
  onPercentChange: (percent: number) => void;
  disabled?: boolean;
};

/**
 * Compact inverse zoom pill + preset menu matching Serenity ZoomControls
 * (Components `986:467`). Candidate for promotion to theme-components.
 */
export function MediaDisplayZoomControls({
  percent,
  onPercentChange,
  disabled = false,
}: MediaDisplayZoomControlsProps) {
  const t = useTimePilotT();
  const [open, setOpen] = useState(false);

  return (
    <Popover
      isOpen={open}
      onOpenChange={setOpen}
      showArrow={false}
      placement="bottom-start"
      sideOffset={8}
      variant="inverted"
      aria-label={t('media.zoomLevel')}
      maxWidth="8rem"
      contentClassName="p-1"
      content={
        <div role="listbox" aria-label={t('media.zoomLevel')} className="flex min-w-[6.5rem] flex-col">
          {FULLSCREEN_ZOOM_PRESETS.map((preset) => {
            const selected = percent === preset;
            return (
              <button
                key={preset}
                type="button"
                role="option"
                aria-selected={selected}
                data-testid={`media-zoom-option-${preset}`}
                className={cn(
                  'flex w-full items-center justify-between gap-4 rounded-[var(--radius-rounded-small,4px)] px-2 py-1',
                  'text-xs font-semibold tracking-[0.36px]',
                  'text-[color:var(--tp-popover-fg,var(--text-primary,#0f1d32))]',
                  'hover:bg-[var(--tp-popover-control-hover-bg,var(--surface-subtle,#f7fafe))]',
                )}
                onClick={() => {
                  onPercentChange(preset);
                  setOpen(false);
                }}
              >
                <span>{preset}%</span>
                {selected ? (
                  <CheckIcon size={16} className="shrink-0" aria-hidden />
                ) : (
                  <span className="size-4 shrink-0" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      }
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={t('media.zoom')}
        aria-haspopup="listbox"
        data-testid="media-zoom-percent"
        className={cn(
          'flex shrink-0 items-center overflow-clip rounded-[var(--radius-rounded-small,4px)]',
          'bg-[var(--color-ink-085,#0f1d32)] p-0.5',
          'text-[color:var(--text-inverse,white)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
      >
        <span className="flex items-center gap-2 px-2 py-1">
          <span className="text-center text-xs font-semibold leading-4 tracking-[0.36px]">
            {percent}%
          </span>
          <CaretDownIcon size={16} className="shrink-0" aria-hidden />
        </span>
      </button>
    </Popover>
  );
}
